// src/stores/user.js
import { defineStore } from 'pinia'
import { Notify } from 'quasar'
import { fbDatabase } from 'src/boot/firebase'
import { logIt } from 'src/functions/function-logger'
import { useProgressStore } from './store-progress'
import { useChatsStore } from 'src/stores/store-chats'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {},
    userGroups: {},
    sort: 'name',
    userDownloaded: false,

    // internal bookkeeping for listeners
    _unsubs: {
      user: null,          // () => off()
      groups: {},          // { [groupId]: () => offAllForThisGroup() }
      groupEvents: {},     // { [groupId]: () => off() }
    },
  }),

  getters: {
    userAccess: (state) => state.user.access,
    uid: (state) => state.user.uid,
    userInfo: (state) => state.user?.info,
    // Teacher-assigned pack suggestions: users/{uid}/suggestedPackIds/{packId}: true
    suggestedPackIds: (state) => state.user?.suggestedPackIds || {},
    // Teacher-set interest tags: users/{uid}/tags/{tag}: true (root of user node, separate from info/tags)
    teacherTags: (state) => state.user?.tags || {},
  },

  actions: {
    fetchOrCreateUser(payload) {
      if (!payload?.uid) return

      // detach any previous user listener (avoid dupes after re-login)
      this._unsubs.user && this._unsubs.user()
      this._unsubs.user = null

      const fbRef = fbDatabase
        .ref('users/')
        .orderByChild('uid')
        .equalTo(payload.uid)
        .limitToFirst(1)

      console.groupCollapsed('[user] fetchOrCreateUser (live listener + initial value)')
      console.time('[user] fetchOrCreateUser time')

      const onValue = async (snapshot) => {
        try {
          if (snapshot.exists()) {
            // same access pattern as original: val()[uid]
            const user = snapshot.val()[payload.uid]
            await this.onUserFetched(user)
          } else {
            await this.createUser(payload)
          }
        } catch (error) {
          logIt({
            error,
            text: `Error in fetchOrCreateUser: ${error?.message || error}`,
            path: `users/`,
            uid: payload.uid,
          })
        } finally {
          console.timeEnd('[user] fetchOrCreateUser time')
          console.groupEnd()
        }
      }

      fbRef.on('value', onValue)
      this._unsubs.user = () => fbRef.off('value', onValue)
    },

    // Keep original write path users/${uid}
    async createUser(user) {
      const payload = {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        photoURL: user.photoURL,
        isAnonymous: user.isAnonymous ? true : null,
      }

      const fbRef = fbDatabase.ref(`users/${payload.uid}`)

      if(import.meta.env.DEV) console.groupCollapsed('[user] createUser')
      if(import.meta.env.DEV) console.time('[user] createUser time')

      try {
        await fbRef.set(payload)
        await this.onUserFetched(payload)
        if (!user.isAnonymous) {
          Notify.create('Yes! Thanks for registering in the Clinic.')
        }
      } catch (error) {
        logIt({
          error,
          text: `Error writing user: ${error?.message || error}`,
          path: `users/${payload.uid}`,
          uid: payload.uid,
        })
      } finally {
        if(import.meta.env.DEV) console.timeEnd('[user] createUser time')
        if(import.meta.env.DEV) console.groupEnd()
      }
    },

    async onUserFetched(user) {
      this.user = user
      this.userDownloaded = true

      const progressStore = useProgressStore()
      const chatsStore = useChatsStore()

      console.groupCollapsed('[user] onUserFetched: bootstrap parallel tasks')
      console.time('[user] bootstrap time')

      const tasks = [
        progressStore.fetchProgress(),
        chatsStore.fetchChats(),
        this.fetchUserGroups(), // resolves after first load per group
      ]

      const results = await Promise.allSettled(tasks)
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn('[user] bootstrap task failed', { index: i, reason: r.reason })
        }
      })

      console.timeEnd('[user] bootstrap time')
      console.groupEnd()
    },

    //USER GROUPS
    async fetchUserGroups() {
      this._detachAllGroupListeners()

      if (!this.user?.groups) return

      console.groupCollapsed('[user] fetchUserGroups (initial loads)')
      console.time('[user] fetchUserGroups time')

      const initialLoads = []

      Object.keys(this.user.groups).forEach((groupId) => {
        // keep original query:
        const ref = fbDatabase
          .ref('groups/')
          .orderByChild('id')
          .equalTo(groupId)
          .limitToFirst(1)

        // initial load promise (first value)
        const initial = ref.once('value').then((snap) => {
          if (snap.exists()) {
            const group = snap.val()[groupId]
            if (group) {
              this.userGroups[group.id] = group
              if (group.activeLesson?.status === 'inProgress') {
                // preserve side-effect if present
                this.setLessonInProgress?.(group.activeLesson)
              }
            }
          }
        })
        initialLoads.push(initial)

        // live updates
        const onValue = (snapshot) => {
          if (snapshot.exists()) {
            const group = snapshot.val()[groupId]
            if (group) {
              this.userGroups[group.id] = group
            }
          }
        }
        ref.on('value', onValue)

        // child removed -> drop from map
        const onChildRemoved = () => {
          delete this.userGroups[groupId]
        }
        ref.on('child_removed', onChildRemoved)

        // single off() that removes both listeners for this group
        this._unsubs.groups[groupId] = () => {
          ref.off('value', onValue)
          ref.off('child_removed', onChildRemoved)
        }
      })

      try {
        await Promise.all(initialLoads)
      } catch (err) {
        logIt({
          error: err,
          text: `Error fetching user groups`,
          uid: this.user.uid,
        })
      } finally {
        console.timeEnd('[user] fetchUserGroups time')
        console.groupEnd()
      }
    },

    // NOTE: kept as-is, with detach safety
    fetchUserGroupEvents(groupId) {
      if (!this.user?.groups || !this.user.groups[groupId]) return

      // detach any prior events listener for this group (avoid dupes)
      this._detachGroupEventsListener(groupId)

      const dbRef = fbDatabase.ref(`group-events/${groupId}`)

      const onValue = (snapshot) => {
        snapshot.forEach((childSnapshot) => {
          const event = childSnapshot.val()
          if (!this.userGroups[groupId]) this.userGroups[groupId] = {}
          if (!this.userGroups[groupId].events) this.userGroups[groupId].events = {}
          this.userGroups[groupId].events[childSnapshot.key] = event
        })
      }
      dbRef.on('value', onValue)

      const onChildRemoved = (snap) => {
        if (this.userGroups[groupId]?.events) {
          delete this.userGroups[groupId].events[snap.key]
        }
      }
      dbRef.on('child_removed', onChildRemoved)

      this._unsubs.groupEvents[groupId] = () => {
        dbRef.off('value', onValue)
        dbRef.off('child_removed', onChildRemoved)
      }
    },

    // ──────────────────────────────────────────────────────────────
    // lifecycle helpers
    // ──────────────────────────────────────────────────────────────
    detachAllUserListeners() {
      this._unsubs.user && this._unsubs.user()
      this._unsubs.user = null
      this._detachAllGroupListeners()
      this._detachAllGroupEvents()
    },

    _detachAllGroupListeners() {
      Object.keys(this._unsubs.groups).forEach((id) => {
        this._unsubs.groups[id]?.()
      })
      this._unsubs.groups = {}
    },

    _detachAllGroupEvents() {
      Object.keys(this._unsubs.groupEvents).forEach((id) => {
        this._unsubs.groupEvents[id]?.()
      })
      this._unsubs.groupEvents = {}
    },

    _detachGroupEventsListener(groupId) {
      this._unsubs.groupEvents[groupId]?.()
      delete this._unsubs.groupEvents[groupId]
    },
  },
})
